import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Layout from '../components/Layout';
import AddPortfolioPropertyModal from '../components/AddPortfolioPropertyModal';
import ComparablesTab from '../components/ComparablesTab';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import ImageCarousel from '../components/ImageCarousel';
import { formatCurrency } from '../utils/formatters';
import { STATUS_CONFIG } from '../config/statusConfig';
import { API_URL } from '../config/api';
import { ChevronLeft, Settings, Trash2, Bell, CheckCircle } from 'lucide-react';
import './PortfolioPropertyDetailsPage.css';

const ALERT_TYPE_LABELS = {
    arv_target: 'ARV target reached',
    arv_vs_list: 'ARV exceeds list price',
    arv_vs_value: 'ARV exceeds value by %',
};

const PortfolioPropertyDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { session } = useAuth();
    const { showToast } = useToast();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    const fetchData = useCallback(async () => {
        if (!session?.access_token) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/portfolio/${id}`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (res.ok) {
                setData(await res.json());
            } else {
                showToast('Failed to load portfolio property', 'error');
                navigate('/portfolio');
            }
        } catch {
            showToast('Network error', 'error');
        } finally {
            setLoading(false);
        }
    }, [id, session, showToast, navigate]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleDelete = async () => {
        try {
            const res = await fetch(`${API_URL}/portfolio/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (res.ok) {
                showToast('Removed from portfolio', 'success');
                navigate('/portfolio');
            }
        } catch {
            showToast('Failed to remove', 'error');
        }
    };

    if (loading) {
        return (
            <Layout scrollable={true}>
                <div className="ppd-page"><div className="ppd-container"><div className="portfolio-loading"><div className="spinner"></div></div></div></div>
            </Layout>
        );
    }

    if (!data) return null;

    const photos = data.primary_photo_url ? [data.primary_photo_url] : [];

    return (
        <Layout scrollable={true}>
            <div className="ppd-page">
                <div className="ppd-container">
                    {/* Back nav */}
                    <button className="ppd-back" onClick={() => navigate('/portfolio')}>
                        <ChevronLeft size={16} /> Back
                    </button>

                    {/* Hero */}
                    <div className="ppd-hero">
                        <div className="ppd-hero-image">
                            {photos.length > 0 ? (
                                <ImageCarousel images={photos} />
                            ) : (
                                <div className="ppd-no-photo">No Photo</div>
                            )}
                            {data.listing_status && data.listing_status !== 'UNKNOWN' && STATUS_CONFIG[data.listing_status] && (
                                <span
                                    className="ppd-status-chip"
                                    style={{ color: STATUS_CONFIG[data.listing_status].color }}
                                >
                                    {STATUS_CONFIG[data.listing_status].label}
                                </span>
                            )}
                        </div>
                        <div className="ppd-hero-info">
                            <h1 className="ppd-address">{data.address || 'Unknown'}</h1>
                            <div className="ppd-specs">
                                {data.beds || '-'} bd · {data.baths || '-'} ba · {data.sqft ? `${Number(data.sqft).toLocaleString()} sqft` : '-'}
                                {data.year_built && ` · Built ${data.year_built}`}
                            </div>
                            <div className="ppd-price-row">
                                <div className="ppd-price-block">
                                    <div className="ppd-price-label">List Price</div>
                                    <div className="ppd-price-value">{formatCurrency(data.list_price)}</div>
                                </div>
                                <div className="ppd-price-block">
                                    <div className="ppd-price-label">Estimated ARV</div>
                                    {data.arv ? (
                                        <div className="ppd-price-value ppd-arv">{formatCurrency(data.arv)}</div>
                                    ) : (
                                        <div className="ppd-price-pending" title="Property is queued for valuation in the next daily run">
                                            Pending valuation
                                        </div>
                                    )}
                                </div>
                                {data.arv && data.list_price && (
                                    <div className="ppd-price-block">
                                        <div className="ppd-price-label">Equity</div>
                                        <div className="ppd-price-value ppd-equity">
                                            {formatCurrency(data.arv - data.list_price)}
                                        </div>
                                    </div>
                                )}
                                {data.estimated_rent && (
                                    <div className="ppd-price-block">
                                        <div className="ppd-price-label">Est. Rent</div>
                                        <div className="ppd-price-value">${Math.round(data.estimated_rent).toLocaleString()}/mo</div>
                                    </div>
                                )}
                            </div>
                            <div className="ppd-actions">
                                <button className="ppd-action-btn" onClick={() => setIsEditOpen(true)}>
                                    <Settings size={14} /> Edit Alerts
                                </button>
                                <button className="ppd-action-btn ppd-action-danger" onClick={() => setIsDeleteOpen(true)}>
                                    <Trash2 size={14} /> Remove
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Alerts card */}
                    <div className="ppd-cards-row">
                        <div className={`ppd-section ${!data.alerts_enabled ? 'ppd-section-muted' : ''}`}>
                            <div className="ppd-section-header-row">
                                <h3 className="ppd-section-title"><Bell size={16} /> Alert Thresholds</h3>
                                <span className={`ppd-status-badge ${data.alerts_enabled ? 'on' : 'off'}`}>
                                    {data.alerts_enabled ? 'Enabled' : 'Paused'}
                                </span>
                            </div>
                            <div className="ppd-alert-list">
                                {data.arv_target_value != null && (
                                    <AlertRow label="ARV reaches" value={formatCurrency(data.arv_target_value)} type="arv_target" triggered={data.triggered_alerts} />
                                )}
                                {data.arv_above_list_amount != null && (
                                    <AlertRow label="ARV exceeds list by" value={formatCurrency(data.arv_above_list_amount)} type="arv_vs_list" triggered={data.triggered_alerts} />
                                )}
                                {data.arv_above_value_pct != null && (
                                    <AlertRow label="ARV exceeds value by" value={`${data.arv_above_value_pct}%`} type="arv_vs_value" triggered={data.triggered_alerts} />
                                )}
                                {data.arv_target_value == null && data.arv_above_list_amount == null && data.arv_above_value_pct == null && (
                                    <p className="ppd-no-alerts">No alert thresholds configured. <button className="ppd-link-btn" onClick={() => setIsEditOpen(true)}>Set up alerts</button></p>
                                )}
                            </div>

                            {data.alert_cc_emails && data.alert_cc_emails.length > 0 && (
                                <div className="ppd-cc-list">
                                    <span className="ppd-cc-label">CC:</span>
                                    <span className="ppd-cc-value">{data.alert_cc_emails.join(', ')}</span>
                                </div>
                            )}

                            {data.triggered_alerts && data.triggered_alerts.length > 0 && (
                                <>
                                    <h3 className="ppd-section-title" style={{ marginTop: 20 }}>Triggered Events</h3>
                                    <div className="ppd-events-list">
                                        {data.triggered_alerts.map((evt, i) => (
                                            <div key={i} className="ppd-event-row">
                                                <CheckCircle size={14} className="ppd-event-icon" />
                                                <span className="ppd-event-type">{ALERT_TYPE_LABELS[evt.alert_type] || evt.alert_type}</span>
                                                <span className="ppd-event-detail">
                                                    ARV was {formatCurrency(evt.arv_at_trigger)} (threshold: {formatCurrency(evt.threshold_value)})
                                                </span>
                                                <span className="ppd-event-date">{new Date(evt.triggered_at).toLocaleDateString()}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                    </div>

                    {/* Notes card (if present) */}
                    {data.notes && (
                        <div className="ppd-section">
                            <h3 className="ppd-section-title">Notes</h3>
                            <p className="ppd-notes">{data.notes}</p>
                        </div>
                    )}

                    {/* Comparables card */}
                    <ComparablesTab portfolioId={id} />
                </div>
            </div>

            {isEditOpen && (
                <AddPortfolioPropertyModal
                    isOpen={isEditOpen}
                    onClose={() => setIsEditOpen(false)}
                    onSuccess={() => { setIsEditOpen(false); fetchData(); }}
                    editMode={true}
                    initialData={data}
                />
            )}

            {isDeleteOpen && (
                <DeleteConfirmationModal
                    isOpen={isDeleteOpen}
                    onClose={() => setIsDeleteOpen(false)}
                    onConfirm={handleDelete}
                    title="Remove from Portfolio"
                    message={`Remove "${data.address || 'this property'}" from your portfolio?`}
                />
            )}
        </Layout>
    );
};


const AlertRow = ({ label, value, type, triggered }) => {
    const isFired = (triggered || []).some(e => e.alert_type === type);
    return (
        <div className={`ppd-alert-row ${isFired ? 'fired' : ''}`}>
            {isFired ? (
                <CheckCircle size={16} className="ppd-alert-icon fired" />
            ) : (
                <Bell size={16} className="ppd-alert-icon armed" />
            )}
            <span className="ppd-alert-label">{label}</span>
            <span className="ppd-alert-value">{value}</span>
            <span className={`ppd-alert-status ${isFired ? 'fired' : 'armed'}`}>
                {isFired ? 'Triggered' : 'Armed'}
            </span>
        </div>
    );
};

export default PortfolioPropertyDetailsPage;
